import React, { useState } from 'react';

import FrontDesk from '../../media/images/modules/front-desk.jpg';
import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Kpis, Panel, Segments, Empty, useFlash, readForm } from '../../ui/kit.js';
import { useBuilding, day, clock } from '../../state/building.js';

const KINDS = ['Guest', 'Vendor', 'Delivery'];
const KIND_TONE = { 'Guest': 'info', 'Vendor': 'warn', 'Delivery': 'muted' };
const STATUS_TONE = { 'Expected': 'info', 'Checked in': 'ok', 'Checked out': 'muted', 'Cancelled': 'muted' };

/* Visitor Management, the front desk's half. Guests a resident registers
   on their screen land on this list, and the desk checks them in and out. */
function VisitorsScreen() {
    const { visitors, patch, add } = useBuilding();
    const [flash, notify] = useFlash();
    const [view, setView] = useState('Today');

    const today = day(0);
    const shown = visitors.filter(v => {
        if (view === 'Today') return v.date === today;
        if (view === 'Upcoming') return v.date !== today && v.status === 'Expected';
        return true;
    });

    const checkIn = v => {
        patch('visitors', v.id, { status: 'Checked in', inAt: clock() });
        notify(`${v.name} checked in. Unit ${v.unit} has been notified.`);
    };

    const checkOut = v => {
        patch('visitors', v.id, { status: 'Checked out', outAt: clock() });
        notify(`${v.name} checked out.`);
    };

    const walkIn = event => {
        const values = readForm(event, ['name', 'unit']);
        if (!values) return notify('Add the visitor name and the unit they are visiting.');

        add('visitors', {
            name: values.name,
            unit: values.unit,
            kind: values.kind,
            date: today,
            note: 'Walk-in, verified by phone with resident',
            status: 'Checked in',
            inAt: clock()
        });
        notify(`${values.name} checked in for unit ${values.unit}. Badge printed.`);
    };

    const todays = visitors.filter(v => v.date === today);
    const count = status => todays.filter(v => v.status === status).length;

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Visitor management" title="Front desk visitor log" />

            <div className="banner">
                <img src={FrontDesk} alt="" />
                <p className="banner__caption">Every guest greeted, logged, and announced at the front desk.</p>
            </div>

            <Kpis items={[
                { label: 'Expected today', value: count('Expected'), tone: 'info' },
                { label: 'On site now', value: count('Checked in'), tone: 'ok' },
                { label: 'Checked out', value: count('Checked out') }
            ]} />

            {flash}

            <div className="split">
                <Panel as="form" className="panel--accent" onSubmit={walkIn}>
                    <h4>Walk-in visitor</h4>
                    <div className="form">
                        <Field label="Visitor name" wide>
                            <input name="name" className="form-control" placeholder="e.g. Marco Delgado" />
                        </Field>
                        <Field label="Unit">
                            <input name="unit" className="form-control" placeholder="e.g. 1502" />
                        </Field>
                        <Field label="Type">
                            <select name="kind" className="form-control" defaultValue="Guest">
                                {KINDS.map(k => <option key={k}>{k}</option>)}
                            </select>
                        </Field>
                        <button type="submit" className="btn btn-primary">Check in now</button>
                    </div>
                </Panel>

                <div className="stack">
                    <Segments label="Show visitors" options={['Today', 'Upcoming', 'All']} value={view} onChange={setView} />
                    {shown.length === 0 ? <Empty>No visitors in this view.</Empty> : (
                        <ul className="rows">
                            {shown.map(v => (
                                <li className="row" key={v.id}>
                                    <div className="row__main">
                                        <span className="row__title">{v.name}</span>
                                        <span className="row__meta">
                                            Unit {v.unit} · {v.date}
                                            {v.note ? ` · ${v.note}` : ''}
                                            {v.inAt ? ` · in ${v.inAt}` : ''}
                                            {v.outAt ? ` · out ${v.outAt}` : ''}
                                        </span>
                                    </div>
                                    <div className="row__end">
                                        <Chip tone={KIND_TONE[v.kind]}>{v.kind}</Chip>
                                        <Chip tone={STATUS_TONE[v.status] || 'muted'}>{v.status}</Chip>
                                        {v.status === 'Expected' && (
                                            <button type="button" className="btn btn-outline-light btn-sm" onClick={() => checkIn(v)}>Check in</button>
                                        )}
                                        {v.status === 'Checked in' && (
                                            <button type="button" className="btn btn-outline-light btn-sm" onClick={() => checkOut(v)}>Check out</button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

export default VisitorsScreen;
